import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, CardMedia, CardContent, Typography, Box,
    IconButton, Chip, Avatar, Stack
} from '@mui/material';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import { ERROR_MESSAGES } from '../../utils/errorMessages';
import { AMENITY_LABELS } from '../../constants/amenities';
import ImageGallery from '../common/ImageGallery';
import { toggleFavoriteRoom } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { event } from '../../utils/analytics';

// 性別の日本語表示用マッピング
const GENDER_LABELS = {
    male: '男性',
    female: '女性',
};

function RoomCard({ room, isOwner, onFavoriteToggle }) {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const { currentUser, userProfile } = useAuth();
    const [isFavorite, setIsFavorite] = useState(false);

    // マウント時とユーザー情報/お気に入り更新時にお気に入り状態を更新
    useEffect(() => {
        if (userProfile?.favorites?.rooms) {
            setIsFavorite(userProfile.favorites.rooms.includes(room._id));
        }
    }, [userProfile?.favorites?.rooms, room._id]);

    const handleCardClick = () => {
        // Google Analyticsへイベント送信
        event({
            category: 'Room',
            action: 'Viewed Room Detail',
            label: room.title || `Room #${room._id}`
        });

        // 既存の遷移処理
        navigate(`/rooms/${room._id}`);
    };

    const handleFavoriteClick = async (e) => {
        e.stopPropagation();
        if (!currentUser) {
            // ユーザーがログインしていない場合
            showNotification('お気に入り追加にはログインが必要です', 'warning');
            return; // 処理を中断
        }

        try {
            await toggleFavoriteRoom(room._id);
            setIsFavorite(!isFavorite);
            showNotification(
                isFavorite ? 'お気に入りから削除しました' : 'お気に入りに追加しました',
                'success'
            );
            if (onFavoriteToggle) {
                onFavoriteToggle();
            }
        } catch (error) {
            showNotification('お気に入りの更新に失敗しました', 'error');
        }
    };

    return (
        <Card
            onClick={handleCardClick}
            sx={{
                cursor: 'pointer',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                },
                transition: 'all 0.3s ease',
                backgroundColor: room.isSample ? 'rgba(0, 0, 0, 0.02)' : '#fff'
            }}
        >
            <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar
                        src={room.owner?.profile?.profileImage}
                        alt={room.owner?.profile?.nickname}
                        sx={{ width: 24, height: 24 }}
                    />
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        minWidth: 0,
                        overflow: 'hidden',
                        width: '100%'
                    }}>
                        <Typography
                            variant="body2"
                            sx={{
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '45%'
                            }}
                        >
                            {room.owner?.profile?.nickname || '未設定'}
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            ml: 'auto',
                            gap: 0.5
                        }}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {room.owner?.profile?.age}歳
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                • {GENDER_LABELS[room.owner?.profile?.gender] || '未設定'}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                • {new Date(room.createdAt).toLocaleDateString()}
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </Box>

            <Box sx={{ position: 'relative', height: 200 }}>
                <ImageGallery
                    images={room.images}
                    height={200}
                    singleImage={true}
                />
                {room.isSample && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            zIndex: 1,
                        }}
                    >
                        サンプル
                    </Box>
                )}
                {!isOwner && (
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            }
                        }}
                        onClick={handleFavoriteClick}
                    >
                        {isFavorite ? <Favorite color="error" /> : <FavoriteBorder />}
                    </IconButton>
                )}
            </Box>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    ¥{room.rentamount.toLocaleString()}/月
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {room.location.prefecture} {room.location.city}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Typography variant="body2">
                        {room.propertyType}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {room.roomType}
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {room.location.station}駅 徒歩{room.location.walkingMinutes}分
                </Typography>
                {room.roomDetails.amenities && room.roomDetails.amenities.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {room.roomDetails.amenities.slice(0, 3).map((amenity) => (
                            <Chip
                                key={amenity}
                                label={AMENITY_LABELS[amenity]}
                                size="small"
                                variant="outlined"
                            />
                        ))}
                        {room.roomDetails.amenities.length > 3 && (
                            <Chip
                                label={`+${room.roomDetails.amenities.length - 3}`}
                                size="small"
                                variant="outlined"
                            />
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

export default RoomCard; 